import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import ErrorMessage from 'components/final-form/ErrorMessage';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import PxGrid from 'components/layout/PxGrid';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import PdfModal from 'components/pdf-modal/PdfModal';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Grid, Header } from 'semantic-ui-react';
import axios from 'services/http';
import {
  getManualPaymentProofListPdfUrl,
  payBills,
  payCcPayedBillsGuarantor,
  payCcPayedBillsPatient
} from 'services/paymentServices';
import styled from 'styled-components';
import { BillStatus, BillType, Privilege } from 'ts-types/api.enums';
import {
  BillPaymentDto,
  BillPaymentRequest,
  BillPaymentResponse,
  BillPaymentSearchResponse,
  PatientDto
} from 'ts-types/api.types';
import { numberFormatter } from 'utils/fomattingUtils';
import { applyStyles } from 'utils/textUtils';
import { isValueValidNumber, mustBeNumber } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .success-message {
    float: right;
    line-height: 1.2rem;
    padding: 0.5rem;
    color: #064673;
  }
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(5) {
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(6) {
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }

  .styled-row-data {
    margin-bottom: 0.8rem;
  }
`;

const TableContainer = styled.div`
  height: 100%;
  min-height: 300px;
`;

const StyledCheckbox = styled(Field)`
  float: right;
  transform: translateX(0%);
  font-weight: 500;
`;

const StyledHeader = styled(Header)`
  margin-top: 0 !important;
`;

const StyledSelectedBills = styled.span`
  padding-left: 1rem;
  font-weight: 300;
  font-size: 1rem;

  .highlight-value {
    color: rgb(48 145 218);
    font-weight: bold;
`;

const errorMessageStyle = {
  marginBottom: "1rem"
};

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const errorRowStyle = {
  color: "#9f3a38"
};

const numberColumnStyle = {
  floating: "right",
  paddingRight: "0.5rem"
};

interface ExtendedBillSearchResponse extends BillPaymentSearchResponse {
  book: boolean
  bookBill: boolean,
  currentPaidAmount: number
}

interface FinalFormData {
  bills: Array<ExtendedBillSearchResponse>,
  selected?: ExtendedBillSearchResponse,
  paymentDate: string,
  proofList: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  bills: Array<BillPaymentSearchResponse>,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  onPatientRefresh: (patient?: PatientDto) => void,
  resetBills: () => void
}

interface State {
  manualPaymentPdfData?: BillPaymentResponse,
  manualPaymentPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  formData: Partial<FinalFormData>
}

class ManualPaymentTableView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {options: {workDate}} = this.props;

    this.state = {
      manualPaymentPdfModalOpen: false,
      cancelTokenSource: axios.CancelToken.source(),
      formData: {
        paymentDate: workDate,
        proofList: false
      }
    }
  }

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData} = this.state;
    const {bills, billsLoading} = this.props;

    if (prevBillsLoading != billsLoading && !billsLoading) {

      const state: any = this.props.location.state;
      const selectedBillId = (state && state.selectedBill) ? state.selectedBill.id : undefined;

      const mappedBills: Array<ExtendedBillSearchResponse> = bills.map(bill => {

        let calculatedPaidAmount = parseFloat(
            ((bill.amount + bill.reminderSurcharge) - bill.paidAmount).toFixed(2));
        calculatedPaidAmount = Math.max(calculatedPaidAmount, 0);

        return {
          ...bill,
          book: !!selectedBillId,
          bookBill: calculatedPaidAmount > 0,
          currentPaidAmount: calculatedPaidAmount
        }
      });

      this.setState({
        formData: {
          paymentDate: formData.paymentDate,
          bills: this.flattenBills(mappedBills),
          selected: undefined
        }
      })
    }
  }

  openManualPaymentPdfModal = (manualPaymentResponse: BillPaymentResponse) => {
    this.setState({
      manualPaymentPdfModalOpen: true,
      manualPaymentPdfData: manualPaymentResponse
    })
  };

  closeManualPaymentPdfModal = () => {
    const proofList = this.state.manualPaymentPdfData!.proofList;
    if (!proofList) {
      this.props.resetBills();
    } else {
      this.setState({
        manualPaymentPdfModalOpen: false,
        manualPaymentPdfData: undefined
      });
    }
  };

  getManualPaymentProofListPdfUrl = (response: BillPaymentResponse) => {
    return getManualPaymentProofListPdfUrl(response.pdfId, response.pdfName);
  }

  modifyBill = (book: boolean, foundBillIndex: number, bills: Array<ExtendedBillSearchResponse>): Partial<FinalFormData> => {

    if (foundBillIndex != -1 && book != undefined) {
      const foundBill = bills[foundBillIndex];

      const modifiedBills = Array.from(bills);

      modifiedBills[foundBillIndex] = {
        ...foundBill,
        bookBill: book ? foundBill.paidAmount + foundBill.currentPaidAmount >= foundBill.amount + foundBill.reminderSurcharge : false
      };

      return {
        bills: modifiedBills
      }
    }

    return {};
  };

  bookDecorator = createDecorator({
    field: /^bills\[.*]\.book$/,
    updates: (...params) => {

      const book: boolean = params[0];
      const {bills} = params[2] as FinalFormData;
      const {bills: oldBills} = params[3] as FinalFormData;

      const foundModifiedIndex = bills
          .findIndex((bill: ExtendedBillSearchResponse, index: number) => bill.book != oldBills[index].book);

      return this.modifyBill(book, foundModifiedIndex, bills);
    }
  });

  showOnlyBookedDecorator = createDecorator({
    field: "showOnlyBooked",
    updates: (...params) => {

      const showOnlyBooked = params[0];
      const {bills} = params[2] as FinalFormData;

      const {bills: oldBills} = this.props;

      if (showOnlyBooked) {
        return {
          bills: bills.filter((bill: ExtendedBillSearchResponse) => bill.book),
          selected: undefined
        }
      }

      const bookedBillsMap: Map<string, ExtendedBillSearchResponse> = new Map<string, ExtendedBillSearchResponse>();

      bills.forEach(bill => {
        bookedBillsMap.set(bill.billId, bill);
      });

      const mappedOldBills = oldBills.map(oldBill => {

        const foundBill = bookedBillsMap.get(oldBill.billId);

        const calculatedPaidAmount = foundBill && foundBill.currentPaidAmount ?
            foundBill.currentPaidAmount : ((oldBill.amount + oldBill.reminderSurcharge) - oldBill.paidAmount).toFixed(2);

        return {
          ...oldBill,
          book: foundBill && foundBill.book,
          bookBill: foundBill && foundBill.bookBill,
          currentPaidAmount: calculatedPaidAmount
        }
      });

      return {
        bills: mappedOldBills,
        selected: undefined
      }
    }
  });

  mapToExtendedBillSearchResponse = (bills: Array<BillPaymentSearchResponse>): Array<ExtendedBillSearchResponse> => {
    return bills
        .map(bill => ({
          ...bill,
          book: false,
          bookBill: false,
          currentPaidAmount: 0
        }));
  }

  flattenBills = (bills: Array<ExtendedBillSearchResponse>): Array<ExtendedBillSearchResponse> => {
    return bills
        .map(bill => [bill, ...this.mapToExtendedBillSearchResponse(bill.collectiveBillParts)])
        .flat(1);
  }

  printProofList = (values: Partial<FinalFormData>) => {
    const newValues = {...values, proofList: true};
    this.handleSubmit(newValues);
  }

  handleMobileAppCcBills = async (billTo: string) => {

    const {getMessage} = this.props;
    const {cancelTokenSource} = this.state;

    let errorMessage;
    const targetMethod = billTo === "patient" ? payCcPayedBillsPatient : payCcPayedBillsGuarantor;
    await targetMethod(cancelTokenSource)
        .then(response => {
          if (response.pdfId) {
            this.openManualPaymentPdfModal(response);
          }
        })
        .catch(() => {
          errorMessage = getMessage("error.general")
        });

    if (errorMessage) {
      return {
        error: errorMessage
      }
    }
  }

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {bills, paymentDate, proofList} = values;

    if (bills && bills.length > 0) {

      const {cancelTokenSource} = this.state;
      const {
        options: {workDate},
        history,
        getMessage,
        currentPatient,
        onPatientRefresh,
        bills: gottenBills,
        currentUser
      } = this.props;

      const bookedBills = bills.filter(bill => bill.book);

      if (bookedBills.length > 0) {

        const hasInvalidPrice = bookedBills.some(bill => !isValueValidNumber(bill.currentPaidAmount));

        if (hasInvalidPrice) {
          return {
            error: getMessage("error.bookedBillInvalidPrice")
          }
        }

        const billPayments: Array<BillPaymentDto> = bookedBills
            .map(bill => {

              const foundBill = gottenBills.find(gottenBill => gottenBill.billId == bill.billId);

              let collectiveBillPartIds: Array<number> = [];
              if (foundBill && foundBill.collectiveBillParts) {
                collectiveBillPartIds = foundBill
                    .collectiveBillParts
                    .map(collectiveBillPart => parseInt(collectiveBillPart.billId.replace("*", "")));
              }

              return {
                billId: parseInt(bill.billId.replace("*", "")),
                paidAmount: bill.currentPaidAmount,
                bookBill: bill.bookBill,
                book: bill.book,
                collectiveBillPartIds: collectiveBillPartIds,
                paymentDate: paymentDate ? paymentDate : workDate,
                importDebtorName: ""
              }
            });

        const billPaymentRequest: BillPaymentRequest = {
          paymentDate: paymentDate ? paymentDate : workDate,
          billPayments: billPayments,
          proofList: !!proofList,
          userId: currentUser!.id,
          mandateId: currentUser!.userConfig.mandateId,
          automaticImport: false
        };

        let errorMessage;
        await payBills(billPaymentRequest, cancelTokenSource)
            .then(response => {
              if (response.pdfId) {
                this.openManualPaymentPdfModal(response);
              } else {
                history.push("/");
                if (currentPatient) {
                  onPatientRefresh(currentPatient)
                }
              }
            })
            .catch(() => {
              errorMessage = getMessage("error.general")
            });

        if (errorMessage) {
          return {
            error: errorMessage
          }
        }
      } else {
        return {
          error: getMessage("error.noBillsToBePaid")
        };
      }
    }
  };

  bookCellRenderer = (data: any) => {

    const {rowIndex, rowData} = data;

    return (
        <Field
            name={`bills[${rowIndex}].book`}
            component={CheckBox}
            disabled={rowData.status == BillStatus.STORNO
            || rowData.status == BillStatus.PAID_CLOSED
            || rowData.type == BillType.PART_OF_COLLECTIVE}
        />
    )
  };

  billStatusCellRenderer = (data: any) => {

    const rowData: ExtendedBillSearchResponse = data.rowData;

    const billIconRequest: BillIconRequest = {
      hasReminders: rowData.hasBillReminders,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  billDateCellRenderer = (data: any) => <div>{moment(data.rowData.date).format("DD.MM.YYYY")}</div>;

  valueCellRenderer = (data: any) => {
    const rowData: ExtendedBillSearchResponse = data.rowData;
    const columnIndex: number = data.columnIndex;

    if (columnIndex == 4) {
      return <div style={numberColumnStyle}>{(rowData.amount)}</div>
    }

    const {currentPaidAmount, book, paidAmount} = rowData;

    let calculatedPaidAmount = rowData.paidAmount;
    if (book && isValueValidNumber(currentPaidAmount)) {
      calculatedPaidAmount = +currentPaidAmount + paidAmount;
    }

    return <div style={numberColumnStyle}>{(calculatedPaidAmount)}</div>
  };

  billsRowGetter = (bills: Array<ExtendedBillSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (form: FormApi, bills: Array<ExtendedBillSearchResponse>) => ({
                                                                                     className,
                                                                                     columns,
                                                                                     index,
                                                                                     key,
                                                                                     style
                                                                                   }: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    const {selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style};

    const {billId, book, currentPaidAmount} = bills[index];

    if (selected && billId == selected.billId) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    if (book) {
      if (isValueValidNumber(currentPaidAmount)) {
        styles.fontWeight = "bold";
      } else {
        styles = {
          ...styles,
          ...errorRowStyle
        }
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
        >
          {columns}
        </div>
    );
  };

  render(): React.ReactNode {
    const {formData, manualPaymentPdfModalOpen, manualPaymentPdfData} = this.state;

    return (
        <StyledFinalFormContainer>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              mutators={{
                ...arrayMutators
              }}
              decorators={[this.bookDecorator, this.showOnlyBookedDecorator]}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
          {
            manualPaymentPdfModalOpen &&
            <PdfModal
                modalOpen={manualPaymentPdfModalOpen}
                onCloseModal={this.closeManualPaymentPdfModal}
                pdfResponses={[{...manualPaymentPdfData}]}
                getPdfUrl={this.getManualPaymentProofListPdfUrl}
            />
          }
        </StyledFinalFormContainer>
    );
  }

  renderForm = ({submitting, values, form, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {bills = [], selected, showOnlyBooked} = values;
    const {getMessage, billsLoading, getMessageWithNamedParams} = this.props;

    let bookedBills = 0;
    let paidAmount = 0;
    let amount = 0;
    let selectedIndex = -1;
    let billClosed = false;
    let billType = BillType.NORMAL;
    let selectedHasPatient = false;

    if (bills.length > 0) {

      if (selected) {
        selectedIndex = bills.findIndex((bill: ExtendedBillSearchResponse) => bill.billId == selected.billId);

        if (selectedIndex != -1) {
          billClosed = [BillStatus.PAID_CLOSED, BillStatus.STORNO].includes(selected.status);

          const {book, currentPaidAmount, paidAmount: billPaidAmount, type} = bills[selectedIndex];
          const {patientName, patientFirstName, patientCity, patientBirthDate} = bills[selectedIndex];

          billType = type as BillType;

          if (!billClosed && book && isValueValidNumber(currentPaidAmount)) {
            paidAmount = +currentPaidAmount + billPaidAmount;
          } else {
            paidAmount = billPaidAmount;
          }

          selectedHasPatient = patientName || patientFirstName || patientCity || patientBirthDate;
        }
      }

      const billsToBePaid = bills.filter((bill: ExtendedBillSearchResponse) => bill.book);

      bookedBills = billsToBePaid.length;
      amount = billsToBePaid
          .filter((bill: ExtendedBillSearchResponse) => bill.currentPaidAmount && isValueValidNumber(bill.currentPaidAmount))
          .map((bill: ExtendedBillSearchResponse) => +bill.currentPaidAmount)
          .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);
    }

    const disabledBtn = bookedBills < 1;

    const countSelectedBills = bills.filter((bill: ExtendedBillSearchResponse) => bill.book).length;
    const billsLength = bills.length;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent>
            <StyledFormContent>
              <StyledContainer style={{width: "60%"}}>
                <Field
                    name="error"
                    component={ErrorMessage}
                    style={errorMessageStyle}
                />
                <StyledHeader>
                  {getMessage("bills")}
                  <StyledSelectedBills>
                    {
                      applyStyles(getMessage("bill.manualPayment.selectedBills")
                          .replace("{{countSelectedBills}}", "" + countSelectedBills)
                          .replace("{{billsLength}}", "" + billsLength)
                      )
                    }
                  </StyledSelectedBills>
                  <StyledCheckbox
                      name="showOnlyBooked"
                      component={CheckBox}
                      label={getMessage("bills.showOnlyBooked")}
                      disabled={bills.length == 0 && !showOnlyBooked}
                  />
                </StyledHeader>
                <TableContainer>
                  {billsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                      : this.renderTable(bills, form)}
                </TableContainer>
                <Header as="h3" textAlign="right">
                  {
                    getMessageWithNamedParams("bills.numberOfBookedBills", {
                      bookedBills: bookedBills,
                      amount: numberFormatter.format(amount)
                    })
                  }
                </Header>
              </StyledContainer>
              <StyledContainer style={{width: "40%"}}>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column width={3} verticalAlign="middle">{getMessage("paymentDate")}:</Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="paymentDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("patient")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selectedHasPatient &&
                  <Grid.Row className={"styled-row-data"}>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                        <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("guarantor")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected && selected.guarantorDescription &&
                  <Grid.Row className={"styled-row-data"}>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.guarantorDescription}`}</div>
                        <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column>
                      <Header>
                        {getMessage("payment")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {
                    selectedIndex != -1 && !billClosed && billType != BillType.PART_OF_COLLECTIVE &&
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={3}></Grid.Column>
                        <Grid.Column width={4}>
                          <Field
                              name={`bills[${selectedIndex}].book`}
                              component={CheckBox}
                              label={getMessage("book")}
                          />
                        </Grid.Column>
                        <Grid.Column width={6}>
                          <Field
                              name={`bills[${selectedIndex}].bookBill`}
                              component={CheckBox}
                              label={getMessage("bookBill")}
                              disabled={!bills[selectedIndex].book}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={3} verticalAlign="middle">{getMessage("amount")}:</Grid.Column>
                        <Grid.Column width={6}>
                          <Field
                              name={`bills[${selectedIndex}].currentPaidAmount`}
                              validate={bills[selectedIndex].book ? mustBeNumber : undefined}
                              component={Input}
                              fluid
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column>
                      <Header>{getMessage("bill.information")}</Header>
                    </Grid.Column>
                  </Grid.Row>
                  {
                    selected &&
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={8} verticalAlign="middle">
                          <Header as="h4">
                            {getMessage("bill.amountHeader")}:
                          </Header>
                        </Grid.Column>
                        <Grid.Column width={8} verticalAlign="middle" textAlign="right">
                          <Header as="h4">
                            {selected.amount}
                          </Header>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={8} verticalAlign="middle">
                          <Header as="h4">
                            {getMessage("amountPaid")}:
                          </Header>
                        </Grid.Column>
                        <Grid.Column width={8} verticalAlign="middle" textAlign="right">
                          <Header as="h4">
                            {paidAmount}
                          </Header>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={8} verticalAlign="middle">
                          <Header as="h4">
                            {getMessage("reminderSurcharge")}:
                          </Header>
                        </Grid.Column>
                        <Grid.Column width={8} verticalAlign="middle" textAlign="right">
                          <Header as="h4">
                            {selected.reminderSurcharge}
                          </Header>
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  }
                </PxGrid>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <FormButtonsRow>
            <Button compact
                    type="button"
                    secondary
                    disabled={submitting || disabledBtn}
                    onClick={() => this.printProofList(values)}>
              {getMessage("collectiveBill.printProoflist")}
            </Button>
            <Button compact type="submit" primary disabled={submitting || disabledBtn}>
              {getMessage("book")}
            </Button>
          </FormButtonsRow>
        </StyledForm>
    )
  };

  userHasPrivilege(privilege: Privilege): boolean {
    const {currentUser} = this.props;
    return currentUser!.userConfig.privileges.includes(privilege)
  }

  renderTable = (bills: Array<ExtendedBillSearchResponse>, form: FormApi): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form, bills)}
            rowCount={bills.length}
            columns={[
              {
                width: 70,
                label: getMessage("book"),
                dataKey: "book",
                cellRenderer: this.bookCellRenderer
              },
              {
                width: 70,
                label: getMessage("bill"),
                dataKey: "status",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                label: "#",
                dataKey: "billId"
              },
              {
                width: 100,
                label: getMessage("bill.date"),
                dataKey: "date",
                cellRenderer: this.billDateCellRenderer
              },
              {
                width: 150,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.valueCellRenderer
              },
              {
                width: 290,
                label: getMessage("paidValue"),
                dataKey: "paidValue",
                cellRenderer: this.valueCellRenderer
              }
            ]}
        />
    )
  };
}

const ManualPaymentTableViewWrapper =
    withMessages(
        withAuthContext(
            withRouterWorkaround(
                ManualPaymentTableView)));

export default ManualPaymentTableViewWrapper;
